// GitHub OAuth start — the single front-door for "(re)authorize with GitHub".
// The backend route /api/v1/auth/github mints a signed state and redirects to
// GitHub; the callback lands the SPA on /auth/callback#... (see Callback.tsx).
// Centralised here so the redirect URL (and the post-auth return memory) never
// drifts across the components that surface it (Login, Landing, ControlRoom,
// Repos re-auth banner).

// sessionStorage key holding the in-app path to return to after a re-auth that
// started from a deep page (e.g. /:account/repos). Why sessionStorage, not a
// query param: the OAuth start route only forwards a signed `app` value into
// the state token — it does NOT round-trip an arbitrary redirect — so the
// return path can't ride the GitHub round trip. Keeping it client-side (same
// browser session) is the contained, FE-only way to come back to where the
// user was. Cleared on consume so it can't leak into a later, unrelated login.
const RETURN_TO_KEY = "github_auth_return_to";

// startGitHubAuth sends the browser to the GitHub OAuth start route. Pass
// `returnTo` (an in-app path like "/machuz/repos") to come back to that exact
// page after the round trip; omit it for the plain sign-in entry points that
// are happy to land on the default post-login page.
export function startGitHubAuth(returnTo?: string): void {
  if (returnTo) {
    try {
      sessionStorage.setItem(RETURN_TO_KEY, returnTo);
    } catch {
      // sessionStorage can throw (private mode / quota). A missing return memory
      // only means we land on the default page after auth — never block re-auth.
    }
  }
  window.location.href = `${window.location.origin}/api/v1/auth/github`;
}

// consumePostAuthReturn reads and clears the stored return path. Returns a same-
// origin in-app path ("/...") or null. The "/" + not "//" guard rejects absolute
// URLs and protocol-relative paths so a stale value can't become an open-redirect
// off-site. Callback uses this only as a fallback, below the backend `redirect`.
export function consumePostAuthReturn(): string | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}
